import React from 'react'
import EntitySentimentButton from 'components/Alignment/EntitySentimentButton'
import EntitySentimentBar from 'components/Alignment/EntitySentimentBar'
import api from 'config/axiosConfig'
import { apiRoutes } from "util/apiRoutes";
import { useUser } from "util/UserContext"

type SentimentValue = -1 | 0 | 1

type SentimentVariant = 'buttons' | 'bar'

interface EntitySentimentProps {
  entityType: 'issue' | 'impact' | 'solution' | 'action' | 'feedback' | 'user' | 'team'
  entityId: number | null | undefined
  className?: string
  variant?: SentimentVariant
}

const EntitySentiment: React.FC<EntitySentimentProps> = ({ entityType, entityId, className, variant = 'buttons' }) => {
  const { currentUser } = useUser()
  const [sentiment, setSentiment] = React.useState<SentimentValue>(0)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [refreshKey, setRefreshKey] = React.useState<number>(0)

  React.useEffect(() => {
    if (!currentUser) {
      setSentiment(0)
      setErrorMessage(null)
      return
    }
    let mounted = true
    const load = async () => {
      if (!entityId) return
      try {
        setLoading(true)
        setErrorMessage(null)
        const { data } = await api.get(apiRoutes.user.entitySentiment(entityType, entityId))
        if (!mounted) return
        if (data && typeof data.value === 'number' && [-1, 0, 1].includes(data.value)) {
          setSentiment(data.value as SentimentValue)
        } else {
          setSentiment(0)
        }
      } catch {
        if (mounted) setSentiment(0)
        if (mounted) setErrorMessage('Could not load your sentiment.')
      } finally {
        if (mounted) setLoading(false)
      }
    }
    void load()
    return () => { mounted = false }
  }, [entityId, entityType, currentUser])

  const applySentiment = async (next: SentimentValue) => {
    if (!entityId || !currentUser) return
    try {
      setLoading(true)
      setErrorMessage(null)
      if (next === 0) {
        await api.delete(apiRoutes.user.entitySentiment(entityType, entityId))
      } else {
        await api.post(apiRoutes.user.entitySentimentRoot, { entityType, entityId, value: next, privacy: 'public' })
      }
      setSentiment(next)
      setRefreshKey(prev => prev + 1)
    } catch {
      setErrorMessage('Could not update your sentiment. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (variant === 'bar') {
    return (
      <div className={`flex flex-col items-start gap-1 ${className || ''}`}>
        <div className="flex items-center gap-0.5 w-fit bg-eoe-icon dark:bg-eoe-dark-icon rounded-full">
          <EntitySentimentButton
            variant="negative"
            sentiment={sentiment}
            loading={loading}
            onToggle={() => applySentiment(sentiment === -1 ? 0 : -1)}
          />
          <EntitySentimentBar entityType={entityType} entityId={entityId} showCounts={false} refreshKey={refreshKey} />
          <EntitySentimentButton
            variant="positive"
            sentiment={sentiment}
            loading={loading}
            onToggle={() => applySentiment(sentiment === 1 ? 0 : 1)}
          />
        </div>
        {errorMessage && (
          <span className="text-xs text-warning-red dark:text-warning-red-dark">
            {errorMessage}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className={`flex flex-col items-center gap-1 ${className || ''}`}>
      <EntitySentimentButton
        variant="positive"
        sentiment={sentiment}
        loading={loading}
        onToggle={() => applySentiment(sentiment === 1 ? 0 : 1)}
      />
      <EntitySentimentButton
        variant="negative"
        sentiment={sentiment}
        loading={loading}
        onToggle={() => applySentiment(sentiment === -1 ? 0 : -1)}
      />
      {errorMessage && (
        <span className="text-xs text-warning-red dark:text-warning-red-dark">
          {errorMessage}
        </span>
      )}
    </div>
  )
}

export default EntitySentiment


